import type { Setter } from 'jotai';

import type { OrchestratorFlowDataT, OrchestratorResponseDataT } from '@consumer-frontend/api';
import {
  isUnauthorizedError,
  trackLoggedOut,
  uiRoutes,
  UNAUTHORIZED_STATUS,
} from '@consumer-frontend/api';

import {
  cnuDrawInputAtom,
  contractsAtom,
  contractStepAlertAtom,
  dirtyStepsAtom,
  flowIdAtom,
  furthestStepIndexAtom,
  isSmartBiddingLeadAtom,
  loanOfferAtom,
  loanOfferLoadedAtAtom,
  orchestratorAccountIdAtom,
  paymentMethodAtom,
} from './atoms';

// The full set of routes the orchestrator is expected to return to this app.
export const ORCHESTRATOR_ROUTES = {
  ACCOUNT_HOME: 'account_home',
  CONTRACT: 'contract',
  DISPLAY_LOAN_OFFER: 'display_loan_offer',
  SIGNED_CONTRACT: 'signed_contract',
  TECHNICAL_ERROR: 'technical_error',
} as const;

// The orchestrator's flow context nests each cnuapp call it made as `{ code, response }` (e.g.
// `account`, `approval`, `contract`, `loan_offer`) — the route/data otherwise still "succeeds"
// even when one of those calls failed, so a 401 buried in any of them has to be checked for
// explicitly rather than inferred from a missing field.
const hasCnuappAuthFailure = (data: OrchestratorFlowDataT): boolean => (
  Object.values(data).some((value) => (
    typeof value === 'object'
    && value !== null
    && 'code' in value
    && value.code === UNAUTHORIZED_STATUS
  ))
);

// 'advance' — safe to move on to the next step (or, on the last step, the submit itself
// succeeded). 'redirecting' — the browser is already navigating away; there is nothing to show
// and nothing further to do here, but this is NOT a failure and must not surface as one.
// 'failed' — a real problem the customer can potentially retry from where they are.
// 'restart' — a contract sign failed before cnuapp decided anything about the contract, so the
// customer retries from the first step (see restartContractFlow).
export type OrchestratorOutcomeT = 'advance' | 'failed' | 'redirecting' | 'restart';

export const SIGN_RETRY_MESSAGE = 'Something went wrong. Please try again later.';

const NOT_FOUND_STATUS = 404;
const REQUEST_TIMEOUT_STATUS = 408;
const TOO_MANY_REQUESTS_STATUS = 429;
const CLIENT_ERROR_STATUS_MIN = 400;
const SERVER_ERROR_STATUS_MIN = 500;

// cnuapp's generate_contract error when the loan offer it was asked for no longer exists, e.g.
// "Loan offer 123 not found".
const LOAN_OFFER_NOT_FOUND_ERROR = /loan offer .* not found/i;

// A cnuapp status that says nothing about the contract itself (cnuapp was too slow, too busy, or
// broken), so the same sign can go through on a retry.
const isTransientStatus = (status: number): boolean => (
  status === REQUEST_TIMEOUT_STATUS
  || status === TOO_MANY_REQUESTS_STATUS
  || status >= SERVER_ERROR_STATUS_MIN
);

const isLoanOfferNotFound = ({ contract }: OrchestratorFlowDataT): boolean => (
  contract?.code === NOT_FOUND_STATUS
  && (contract.response?.errors ?? []).some((error) => LOAN_OFFER_NOT_FOUND_ERROR.test(error))
);

/**
 * Resets every flow atom — flowId, step progress, and the Funds inputs are backed by
 * sessionStorage so a mid-flow refresh doesn't lose them; loanOfferAtom and contractsAtom are
 * plain in-memory atoms (current_step is guaranteed to return both fresh for whatever step the
 * flow is on, so neither needs to survive a reload — see their doc comments in atoms.ts) but are
 * reset here anyway for consistency. This all has to be torn down explicitly whenever the flow is
 * genuinely done: on the way out of the app, on logout, or once a load/reload fails in a way this
 * app can't recover from. Left in place, the sessionStorage-backed pieces would sit in browser
 * storage well past when the flow that needed them ended, and a customer who returns to this
 * origin in the same tab (e.g. the back button, or a page restored from bfcache) would resume a
 * flow they already left instead of landing back on "no flow to resume."
 */
export const clearFlowStorage = (set: Setter): void => {
  set(flowIdAtom, '');
  set(isSmartBiddingLeadAtom, false);
  set(furthestStepIndexAtom, 0);
  set(loanOfferAtom, null);
  set(loanOfferLoadedAtAtom, null);
  set(contractsAtom, []);
  set(cnuDrawInputAtom, '');
  set(paymentMethodAtom, null);
  set(dirtyStepsAtom, {});
  set(orchestratorAccountIdAtom, '');
};

/**
 * The single path off this app, used for every reason the customer might leave it (missing/
 * invalid flowId, a completed sign, an expired session, an orchestrator route this app has no
 * screen for, ...). See clearFlowStorage for why the storage teardown has to happen here rather
 * than being left for the browser to eventually discard.
 */
export const leaveContractApp = (set: Setter, url: string): void => {
  clearFlowStorage(set);

  window.location.href = url;
};

/**
 * For a request that failed outright (the orchestrator/cnuapp call itself returned 401, e.g. an
 * expired session), rather than "succeeding" with a 401 buried inside its body — same
 * destination as the buried-401 case in processOrchestratorResponse below. Returns whether it
 * redirected, so callers can fall back to their normal error handling otherwise.
 */
export const redirectIfUnauthorized = (error: unknown, set: Setter): boolean => {
  if (!isUnauthorizedError(error)) return false;

  leaveContractApp(set, uiRoutes.loginInactivity);

  return true;
};

/**
 * For a contract sign that failed before cnuapp decided anything about the contract — cnuapp or
 * the orchestrator itself was down, so the customer can try again. Leaves the retry alert for the
 * first step to show, and pulls step progress back to it: re-submitting that step is what puts the
 * orchestrator flow back on the contract step, so the later steps stay locked until it is. The
 * Funds inputs are kept, so the customer's selections survive the retry. Navigating there is up to
 * ContractStep (see its 'restart' handling), since this has no router.
 */
export const restartContractFlow = (set: Setter): OrchestratorOutcomeT => {
  set(furthestStepIndexAtom, 0);
  set(contractStepAlertAtom, SIGN_RETRY_MESSAGE);
  set(contractsAtom, []);

  return 'restart';
};

/**
 * A failed submit_contract falls back to the `contract` route with cnuapp's own `{ code, response }`
 * (plus an `error` list when cnuapp never answered) at the top level of `data`. A successful step
 * submit that lands on `contract` nests each cnuapp call under its own key instead, so it never
 * carries a failing top-level `code`.
 */
const processContractRoute = (data: OrchestratorFlowDataT, set: Setter): OrchestratorOutcomeT => {
  const { code, contract, error } = data;

  if (typeof code !== 'number' || code < CLIENT_ERROR_STATUS_MIN || contract) return 'advance';

  if (error !== undefined || isTransientStatus(code)) return restartContractFlow(set);

  // cnuapp rejected the sign as unauthorized (the loan offer belongs to a different customer), so
  // end the session: cnufe's /logout signs the customer out and lands on cashnetusa.com.
  if (code === UNAUTHORIZED_STATUS) {
    trackLoggedOut('sign_unauthorized');
    leaveContractApp(set, uiRoutes.logOut);

    return 'redirecting';
  }

  // cnuapp turned the sign down for a reason the customer can't fix from here (offer not found,
  // funding date expired, schedule offer not accepted, the loan can't be finalized, bad payment
  // method, ...), so they start over from account home — with no banner.
  leaveContractApp(set, uiRoutes.accountHome);

  return 'redirecting';
};

/**
 * Mirrors web-acquisitions' processOrchestratorResponse/setOrchestratorData.
 *
 * Data fields (loan_offer, contract) are mirrored into the store whenever present, regardless
 * of `route` — e.g. switching repayment methods returns route "contract" but still carries a
 * freshly recalculated loan_offer alongside it, and both need to land in the store.
 *
 * `route` is used only to send the customer to the same destination web-acquisitions would for
 * the routes this app has no screen for (signed_contract, account_home, technical_error), and to
 * route a contract sign cnuapp failed or rejected (see processContractRoute).
 *
 * Takes a jotai `Setter` rather than writing through a global store so callers keep whatever
 * store scope they're already rendering under (e.g. Storybook's per-story isolated stores).
 *
 * Returns an OrchestratorOutcomeT rather than a plain boolean specifically so a redirect (the
 * browser navigating away, e.g. a successful contract sign) can be told apart from a genuine
 * failure (an orchestrator `errors` array, or a route this app doesn't know how to handle) —
 * conflating the two previously meant a successful sign showed an error banner and re-enabled
 * the submit button while the page was mid-navigation.
 */
export const processOrchestratorResponse = (
  response: OrchestratorResponseDataT<OrchestratorFlowDataT>,
  set: Setter,
): OrchestratorOutcomeT => {
  // A cnuapp session can expire mid-flow even though the orchestrator call itself "succeeds" —
  // send the customer back to log in rather than rendering a page with missing data.
  if (hasCnuappAuthFailure(response.data)) {
    trackLoggedOut('cnuapp_unauthorized');
    leaveContractApp(set, uiRoutes.loginInactivity);

    return 'redirecting';
  }

  if (response.errors.length > 0) {
    console.warn('Orchestrator returned errors:', response.errors);

    return 'failed';
  }

  // A failed cnuapp call arrives under the same key without its payload (e.g. generate_contract
  // while cnuapp is down, when re-submitting the first step after a failed sign) — copying that
  // through would put `undefined` in the store, and the atoms derived from contractsAtom would
  // throw mid-render.
  const loanOffer = response.data.loan_offer?.response?.metadata;

  if (loanOffer) {
    set(loanOfferAtom, loanOffer);
  }

  if (typeof response.data.sb_lead === 'boolean') {
    set(isSmartBiddingLeadAtom, response.data.sb_lead);
  }

  const contracts = response.data.contract?.response?.contracts;

  if (response.data.contract && !Array.isArray(contracts)) {
    // The loan offer behind this flow no longer exists in cnuapp, so no retry can ever produce its
    // contract — the customer starts over from account home, with no banner.
    if (
      response.route === ORCHESTRATOR_ROUTES.DISPLAY_LOAN_OFFER
      && isLoanOfferNotFound(response.data)
    ) {
      leaveContractApp(set, uiRoutes.accountHome);

      return 'redirecting';
    }

    // generate_contract failed — never let the customer reach a contract cnuapp didn't just produce
    return restartContractFlow(set);
  }

  if (Array.isArray(contracts)) {
    set(contractsAtom, contracts);
  }

  if (typeof response.data.account_id === 'string' && response.data.account_id) {
    set(orchestratorAccountIdAtom, response.data.account_id);
  }

  switch (response.route) {
    case ORCHESTRATOR_ROUTES.SIGNED_CONTRACT:
      leaveContractApp(set, uiRoutes.contractSubmitted);

      return 'redirecting';

    case ORCHESTRATOR_ROUTES.ACCOUNT_HOME:
      leaveContractApp(set, uiRoutes.accountHome);

      return 'redirecting';

    case ORCHESTRATOR_ROUTES.TECHNICAL_ERROR:
      leaveContractApp(set, uiRoutes.accountHome);

      return 'redirecting';

    // Handled above via field presence — no navigation of their own.
    case ORCHESTRATOR_ROUTES.DISPLAY_LOAN_OFFER:
      return 'advance';

    case ORCHESTRATOR_ROUTES.CONTRACT:
      return processContractRoute(response.data, set);

    default:
      console.warn('Unhandled orchestrator route:', response.route);

      return 'failed';
  }
};

/**
 * Translates an OrchestratorOutcomeT into the boolean-'redirecting'-or-'restart' contract
 * ContractStep's onContinue expects. Kept as one shared mapping — rather than each page
 * re-deriving it — so LocFundsPage and LocAuthPage can't drift out of sync on what each outcome
 * means for the Continue button.
 */
export const toContinueResult = (outcome: OrchestratorOutcomeT): boolean | 'redirecting' | 'restart' => {
  if (outcome === 'redirecting' || outcome === 'restart') return outcome;

  return outcome === 'advance';
};
